import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User
} from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  type Timestamp
} from "firebase/firestore";
import {
  ArrowLeft,
  Calendar,
  DollarSign,
  FileSpreadsheet,
  Lightbulb,
  Lock,
  LogOut,
  MessageSquare,
  PieChart,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users
} from "lucide-react";
import { Link } from "react-router-dom";
import { auth, db, googleProvider } from "../../lib/firebase";
import { applyPageMetadata } from "../../lib/seo";

// Strictly whitelist admin emails
const WHITELIST = ["breakrot@gmail.com", "muhamadwildanfaz@gmail.com"];

interface DailyActivities {
  transactions: number;
  budgets: number;
  ai_chats: number;
  exports: number;
  purchases: number;
  hadir: number;
  lastUpdated: Date | null;
}

interface FeedbackItem {
  id: string;
  category: string;
  message: string;
  isAnonymous: boolean;
  userName: string;
  appVersion: string;
  createdAt: string;
  timestamp?: Timestamp;
}

function formatDateToId(date: Date): string {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

export function SteriaCashAnalyticsPage() {
  const [user, setUser] = useState<User | null>(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [authError, setAuthError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [activities, setActivities] = useState<DailyActivities>({
    transactions: 0,
    budgets: 0,
    ai_chats: 0,
    exports: 0,
    purchases: 0,
    hadir: 0,
    lastUpdated: null,
  });
  const [feedbackList, setFeedbackList] = useState<FeedbackItem[]>([]);
  const [cooldown, setCooldown] = useState(0);
  const cooldownTimer = useRef<number | null>(null);

  useEffect(() => {
    applyPageMetadata({
      title: "SteriaCash Analytics | Steria House",
      description: "Private executive analytics and general activity dashboard for SteriaCash.",
      canonicalPath: "/steriacash",
      noindex: true,
    });
  }, []);

  const fetchDashboardData = useCallback(async (targetDate: Date) => {
    setLoading(true);
    const dateId = formatDateToId(targetDate);

    try {
      // 1. Fetch Daily Anonymous Activities
      const docRef = doc(db, "activities", dateId);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const data = snap.data();
        const lastUpdated = data.lastUpdated
          ? typeof data.lastUpdated.toDate === "function"
            ? data.lastUpdated.toDate()
            : new Date(data.lastUpdated)
          : null;

        setActivities({
          transactions: Number(data.transactions || 0),
          budgets: Number(data.budgets || 0),
          ai_chats: Number(data.ai_chats || 0),
          exports: Number(data.exports || 0),
          purchases: Number(data.purchases || 0),
          hadir: Number(data.hadir || 0),
          lastUpdated,
        });
      } else {
        setActivities({
          transactions: 0,
          budgets: 0,
          ai_chats: 0,
          exports: 0,
          purchases: 0,
          hadir: 0,
          lastUpdated: null,
        });
      }

      // 2. Fetch Latest Feedback
      const feedbackQuery = query(
        collection(db, "feedback"),
        orderBy("timestamp", "desc"),
        limit(20)
      );
      const feedbackSnap = await getDocs(feedbackQuery);
      const list: FeedbackItem[] = feedbackSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() as Omit<FeedbackItem, "id">),
      }));
      setFeedbackList(list);
    } catch (err: unknown) {
      console.error("Failed to load SteriaCash analytics:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const startCooldown = () => {
    setCooldown(15);
    if (cooldownTimer.current) window.clearInterval(cooldownTimer.current);
    cooldownTimer.current = window.setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownTimer.current) window.clearInterval(cooldownTimer.current);
          cooldownTimer.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (cooldownTimer.current) window.clearInterval(cooldownTimer.current);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setAuthChecking(false);
      if (currentUser) {
        const email = currentUser.email?.toLowerCase() || "";
        if (!WHITELIST.includes(email)) {
          setAuthError(`Akses Ditolak: Akun (${email}) tidak terdaftar dalam whitelist admin.`);
          await signOut(auth);
          setUser(null);
          return;
        }
        setAuthError("");
        setUser(currentUser);
        fetchDashboardData(selectedDate);
      } else {
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, [fetchDashboardData, selectedDate]);

  const handleLogin = async () => {
    try {
      setAuthError("");
      await signInWithPopup(auth, googleProvider);
    } catch (err: unknown) {
      const e = err as Error;
      setAuthError(e.message || "Gagal masuk dengan Google.");
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
  };

  const handleRefresh = () => {
    if (cooldown === 0 && !loading) {
      fetchDashboardData(selectedDate);
      startCooldown();
    }
  };

  if (authChecking) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0b101b", color: "#fff" }}>
        <p style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <RefreshCw className="animate-spin" size={20} /> Memeriksa autentikasi...
        </p>
      </div>
    );
  }

  // Not Logged In Screen
  if (!user) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "radial-gradient(circle at 50% 20%, #172038 0%, #080d1a 100%)", padding: 20 }}>
        <div style={{ maxWidth: 440, width: "100%", background: "rgba(18, 26, 47, 0.85)", border: "1px solid rgba(255, 255, 255, 0.12)", borderRadius: 24, padding: "40px 32px", textAlign: "center", boxShadow: "0 24px 60px rgba(0,0,0,0.5)", backdropFilter: "blur(20px)" }}>
          <div style={{ width: 64, height: 64, borderRadius: 18, background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 20, boxShadow: "0 10px 24px rgba(16, 185, 129, 0.3)" }}>
            <Lock size={32} color="#000" />
          </div>

          <h1 style={{ color: "#ffffff", fontSize: "1.6rem", fontWeight: 800, margin: "0 0 8px" }}>
            SteriaCash Analytics
          </h1>
          <p style={{ color: "rgba(255, 255, 255, 0.65)", fontSize: "0.9rem", lineHeight: 1.5, margin: "0 0 28px" }}>
            Portal analitik privat SteriaCash. Hanya menerima autentikasi akun Google terverifikasi (<strong>breakrot@gmail.com</strong>).
          </p>

          <button
            onClick={handleLogin}
            style={{
              width: "100%",
              height: 50,
              borderRadius: 14,
              border: "1px solid rgba(255, 255, 255, 0.2)",
              background: "#ffffff",
              color: "#111827",
              fontWeight: 700,
              fontSize: "0.95rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              cursor: "pointer",
              boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
              transition: "transform 0.15s ease",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Masuk dengan Google
          </button>

          {authError && (
            <div style={{ marginTop: 20, padding: 12, borderRadius: 10, background: "rgba(239, 68, 68, 0.15)", border: "1px solid rgba(239, 68, 68, 0.3)", color: "#fca5a5", fontSize: "0.84rem", lineHeight: 1.4 }}>
              {authError}
            </div>
          )}

          <div style={{ marginTop: 28, borderTop: "1px solid rgba(255, 255, 255, 0.08)", paddingTop: 18 }}>
            <Link to="/" style={{ color: "rgba(255, 255, 255, 0.5)", fontSize: "0.82rem", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <ArrowLeft size={14} /> Kembali ke Steria House
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0a0f1d", color: "#f1f5f9", padding: "24px 20px 80px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Top Header */}
        <header style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 16, paddingBottom: 24, borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <Link to="/#products" style={{ color: "#94a3b8", display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.82rem" }}>
                <ArrowLeft size={14} /> Steria House
              </Link>
              <span style={{ color: "#475569" }}>•</span>
              <span style={{ background: "rgba(16, 185, 129, 0.18)", color: "#34d399", border: "1px solid rgba(16, 185, 129, 0.35)", borderRadius: 999, padding: "2px 10px", fontSize: "0.72rem", fontWeight: 700 }}>
                LIVE ANALYTICS
              </span>
            </div>
            <h1 style={{ fontSize: "1.85rem", fontWeight: 900, color: "#ffffff", margin: 0 }}>
              SteriaCash Dashboard
            </h1>
            <p style={{ color: "#94a3b8", fontSize: "0.88rem", margin: "4px 0 0" }}>
              Ringkasan aktivitas anonim, kotak saran pengguna, dan performa aplikasi.
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(30, 41, 59, 0.7)", border: "1px solid rgba(255, 255, 255, 0.1)", borderRadius: 12, padding: "6px 12px" }}>
              <ShieldCheck size={16} color="#34d399" />
              <span style={{ fontSize: "0.82rem", color: "#e2e8f0", fontWeight: 600 }}>{user.email}</span>
            </div>

            <button
              onClick={handleRefresh}
              disabled={loading || cooldown > 0}
              style={{
                background: "rgba(16, 185, 129, 0.15)",
                border: "1px solid rgba(16, 185, 129, 0.4)",
                color: "#34d399",
                borderRadius: 12,
                padding: "8px 14px",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontWeight: 700,
                fontSize: "0.82rem",
                cursor: cooldown > 0 ? "not-allowed" : "pointer",
                opacity: cooldown > 0 ? 0.6 : 1,
              }}
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              {cooldown > 0 ? `${cooldown}s` : "Segarkan"}
            </button>

            <button
              onClick={handleLogout}
              style={{
                background: "rgba(239, 68, 68, 0.12)",
                border: "1px solid rgba(239, 68, 68, 0.3)",
                color: "#f87171",
                borderRadius: 12,
                padding: "8px 12px",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: "0.82rem",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <LogOut size={14} /> Keluar
            </button>
          </div>
        </header>

        {/* Date Selector & Privacy Notice */}
        <section style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 14, margin: "24px 0 20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Calendar size={18} color="#34d399" />
            <span style={{ fontSize: "0.92rem", fontWeight: 700 }}>Periode Tanggal:</span>
            <input
              type="date"
              value={selectedDate.toISOString().split("T")[0]}
              onChange={(e) => {
                if (e.target.value) {
                  const d = new Date(e.target.value);
                  setSelectedDate(d);
                  fetchDashboardData(d);
                }
              }}
              style={{
                background: "#1e293b",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                color: "#fff",
                borderRadius: 10,
                padding: "6px 12px",
                fontSize: "0.88rem",
              }}
            />
            <button
              onClick={() => {
                const today = new Date();
                setSelectedDate(today);
                fetchDashboardData(today);
              }}
              style={{
                background: "#334155",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                color: "#e2e8f0",
                borderRadius: 10,
                padding: "6px 12px",
                fontSize: "0.82rem",
                fontWeight: 600,
              }}
            >
              Hari Ini ({formatDateToId(new Date())})
            </button>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(56, 189, 248, 0.08)", border: "1px solid rgba(56, 189, 248, 0.2)", borderRadius: 12, padding: "6px 14px" }}>
            <Lock size={14} color="#38bdf8" />
            <span style={{ fontSize: "0.78rem", color: "#7dd3fc" }}>
              100% Anonim & Agregat — Tidak ada data transaksi/saldo pribadi user yang dikumpulkan
            </span>
          </div>
        </section>

        {/* 6 Key Activity Metric Cards */}
        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 32 }}>
          {/* Card 1: Transactions */}
          <div style={{ background: "rgba(19, 27, 49, 0.8)", border: "1px solid rgba(255, 255, 255, 0.08)", borderRadius: 18, padding: "20px 18px", backdropFilter: "blur(10px)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <span style={{ fontSize: "0.82rem", color: "#94a3b8", fontWeight: 700 }}>TRANSAKSI DIBUAT</span>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: "rgba(16, 185, 129, 0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <TrendingUp size={18} color="#34d399" />
              </div>
            </div>
            <div style={{ fontSize: "2rem", fontWeight: 900, color: "#ffffff" }}>
              {activities.transactions.toLocaleString()}
            </div>
            <small style={{ color: "#64748b", fontSize: "0.74rem" }}>Aktivitas catat & edit transaksi</small>
          </div>

          {/* Card 2: Budgets */}
          <div style={{ background: "rgba(19, 27, 49, 0.8)", border: "1px solid rgba(255, 255, 255, 0.08)", borderRadius: 18, padding: "20px 18px", backdropFilter: "blur(10px)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <span style={{ fontSize: "0.82rem", color: "#94a3b8", fontWeight: 700 }}>ATUR BUDGET</span>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: "rgba(245, 158, 11, 0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <PieChart size={18} color="#fbbf24" />
              </div>
            </div>
            <div style={{ fontSize: "2rem", fontWeight: 900, color: "#ffffff" }}>
              {activities.budgets.toLocaleString()}
            </div>
            <small style={{ color: "#64748b", fontSize: "0.74rem" }}>Pengaturan & limit anggaran</small>
          </div>

          {/* Card 3: AI Chats */}
          <div style={{ background: "rgba(19, 27, 49, 0.8)", border: "1px solid rgba(255, 255, 255, 0.08)", borderRadius: 18, padding: "20px 18px", backdropFilter: "blur(10px)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <span style={{ fontSize: "0.82rem", color: "#94a3b8", fontWeight: 700 }}>KONSULTASI AI</span>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: "rgba(168, 85, 247, 0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Sparkles size={18} color="#c084fc" />
              </div>
            </div>
            <div style={{ fontSize: "2rem", fontWeight: 900, color: "#ffffff" }}>
              {activities.ai_chats.toLocaleString()}
            </div>
            <small style={{ color: "#64748b", fontSize: "0.74rem" }}>Pertanyaan ke asisten Steria AI</small>
          </div>

          {/* Card 4: Exports */}
          <div style={{ background: "rgba(19, 27, 49, 0.8)", border: "1px solid rgba(255, 255, 255, 0.08)", borderRadius: 18, padding: "20px 18px", backdropFilter: "blur(10px)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <span style={{ fontSize: "0.82rem", color: "#94a3b8", fontWeight: 700 }}>EKSPOR LAPORAN</span>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: "rgba(56, 189, 248, 0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <FileSpreadsheet size={18} color="#38bdf8" />
              </div>
            </div>
            <div style={{ fontSize: "2rem", fontWeight: 900, color: "#ffffff" }}>
              {activities.exports.toLocaleString()}
            </div>
            <small style={{ color: "#64748b", fontSize: "0.74rem" }}>Unduhan file PDF & CSV</small>
          </div>

          {/* Card 5: User Sessions */}
          <div style={{ background: "rgba(19, 27, 49, 0.8)", border: "1px solid rgba(255, 255, 255, 0.08)", borderRadius: 18, padding: "20px 18px", backdropFilter: "blur(10px)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <span style={{ fontSize: "0.82rem", color: "#94a3b8", fontWeight: 700 }}>PENGGUNA AKTIF</span>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: "rgba(236, 72, 153, 0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Users size={18} color="#f472b6" />
              </div>
            </div>
            <div style={{ fontSize: "2rem", fontWeight: 900, color: "#ffffff" }}>
              {activities.hadir.toLocaleString()}
            </div>
            <small style={{ color: "#64748b", fontSize: "0.74rem" }}>Sesi buka aplikasi harian</small>
          </div>

          {/* Card 6: Purchases */}
          <div style={{ background: "rgba(19, 27, 49, 0.8)", border: "1px solid rgba(255, 255, 255, 0.08)", borderRadius: 18, padding: "20px 18px", backdropFilter: "blur(10px)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <span style={{ fontSize: "0.82rem", color: "#94a3b8", fontWeight: 700 }}>PEMBELIAN PRO</span>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: "rgba(251, 191, 36, 0.18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <DollarSign size={18} color="#fbbf24" />
              </div>
            </div>
            <div style={{ fontSize: "2rem", fontWeight: 900, color: "#ffffff" }}>
              {activities.purchases.toLocaleString()}
            </div>
            <small style={{ color: "#64748b", fontSize: "0.74rem" }}>Konfirmasi langganan PRO</small>
          </div>
        </section>

        {/* Feedback / Kotak Saran Feed Section */}
        <section style={{ background: "rgba(19, 27, 49, 0.8)", border: "1px solid rgba(255, 255, 255, 0.08)", borderRadius: 24, padding: "28px 24px", backdropFilter: "blur(10px)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div>
              <h2 style={{ fontSize: "1.25rem", fontWeight: 800, color: "#ffffff", margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
                <MessageSquare size={20} color="#34d399" /> Kotak Saran & Masukan Pengguna
              </h2>
              <p style={{ color: "#94a3b8", fontSize: "0.84rem", margin: "4px 0 0" }}>
                Masukan langsung dari aplikasi SteriaCash via Google Play & in-app feedback.
              </p>
            </div>
            <span style={{ fontSize: "0.8rem", color: "#94a3b8", background: "rgba(255, 255, 255, 0.06)", padding: "4px 12px", borderRadius: 999 }}>
              Total: {feedbackList.length} masukan terbaru
            </span>
          </div>

          {feedbackList.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "#64748b" }}>
              <Lightbulb size={36} style={{ margin: "0 auto 10px", opacity: 0.4 }} />
              <p style={{ margin: 0 }}>Belum ada masukan baru yang tercatat.</p>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {feedbackList.map((item) => {
                const isError = item.category?.toLowerCase().includes("error") || item.category?.toLowerCase().includes("bug");
                const isSaran = item.category?.toLowerCase().includes("saran") || item.category?.toLowerCase().includes("ide");
                const badgeColor = isError ? "#f87171" : isSaran ? "#fbbf24" : "#38bdf8";
                const badgeBg = isError ? "rgba(239, 68, 68, 0.15)" : isSaran ? "rgba(245, 158, 11, 0.15)" : "rgba(56, 189, 248, 0.15)";

                return (
                  <div
                    key={item.id}
                    style={{
                      background: "rgba(30, 41, 59, 0.5)",
                      border: "1px solid rgba(255, 255, 255, 0.06)",
                      borderRadius: 16,
                      padding: "16px 18px",
                      display: "grid",
                      gap: 8,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ background: badgeBg, color: badgeColor, border: `1px solid ${badgeColor}33`, borderRadius: 8, padding: "2px 8px", fontSize: "0.72rem", fontWeight: 700 }}>
                          {item.category || "Masukan Umum"}
                        </span>
                        <span style={{ fontSize: "0.82rem", color: "#94a3b8", display: "flex", alignItems: "center", gap: 4 }}>
                          {item.isAnonymous ? "🔒 Anonim" : `👤 ${item.userName || "User"}`}
                        </span>
                      </div>
                      <span style={{ fontSize: "0.75rem", color: "#64748b" }}>
                        {item.createdAt || (item.timestamp?.toDate ? item.timestamp.toDate().toLocaleString() : "-")}
                      </span>
                    </div>

                    <p style={{ color: "#e2e8f0", fontSize: "0.9rem", lineHeight: 1.5, margin: 0 }}>
                      {item.message}
                    </p>

                    <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: "0.72rem", color: "#64748b" }}>
                      <span>Versi App: {item.appVersion || "1.0.6"}</span>
                      <span>•</span>
                      <span>Platform: Android</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
